import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { FileText, Settings, Clock, LogOut, TrendingUp } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminProvider } from './contexts/AdminContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import Login from './components/Login';
import AuthCallback from './components/AuthCallback';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import SubscribePage from './components/SubscribePage';
import InviteSignup from './components/InviteSignup';
import Analytics from './components/Analytics';
import QuoteForm from './components/QuoteForm';
import QuotePreview from './components/QuotePreview';
import SettingsPage from './components/SettingsPage';
import QuoteHistory from './components/QuoteHistory';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import './App.css';

const Navigation = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const navRef = useRef(null);
  
  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show navbar when at the top of the page
      if (currentScrollY < 10) {
        setIsVisible(true);
      }
      // Hide when scrolling down, show when scrolling up
      else if (currentScrollY > lastScrollY && currentScrollY > 60) {
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    const updateNavHeight = () => {
      if (!navRef.current) return;
      const height = navRef.current.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--app-nav-height', `${Math.ceil(height)}px`);
    };

    updateNavHeight();
    window.addEventListener('resize', updateNavHeight);
    return () => window.removeEventListener('resize', updateNavHeight);
  }, [user, location.pathname]);
  
  return (
    <nav
      ref={navRef}
      className="app-nav"
      style={{
        transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 0.3s ease-in-out'
      }}
    >
      <div className="nav-container">
        <Link to={user ? '/' : '/login'} className="nav-brand" aria-label="Go to QuotePro home">
          <FileText size={32} strokeWidth={2} />
          <h1>QuotePro</h1>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          {user && (
            <div className="nav-links">
            <Link 
              to="/" 
              className={`nav-link ${isActive('/') ? 'active' : ''}`}
            >
              <FileText size={20} />
              <span>New Quote</span>
            </Link>
            <Link 
              to="/history" 
              className={`nav-link ${isActive('/history') ? 'active' : ''}`}
            >
              <Clock size={20} />
              <span>History</span>
            </Link>
            <Link 
              to="/analytics" 
              className={`nav-link ${isActive('/analytics') ? 'active' : ''}`}
            >
              <TrendingUp size={20} />
              <span>Analytics</span>
            </Link>
            <Link 
              to="/settings" 
              className={`nav-link ${isActive('/settings') ? 'active' : ''}`}
            >
              <Settings size={20} />
              <span>Settings</span>
            </Link>
            </div>
          )}
          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {user.email === 'gonzalezdevir@gmail.com' && (
                <Link to="/admin/dashboard" className="nav-link">
                  <Settings size={20} />
                  <span>Admin Portal</span>
                </Link>
              )}
              <span style={{ color: '#666', fontSize: '0.95rem' }}>{user.name}</span>
              <button onClick={handleLogout} className="nav-link" style={{ cursor: 'pointer' }}>
                <LogOut size={20} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in URL fragment (synchronous check)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  // Admin routes - no navigation bar
  if (location.pathname.startsWith('/admin')) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
      </Routes>
    );
  }
  
  return (
    <>
      <Navigation />
      <main className="app-main">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth-callback" element={<AuthCallback />} />
          <Route path="/subscribe" element={<SubscribePage />} />
          <Route path="/invite" element={<InviteSignup />} />
          <Route path="/" element={<ProtectedRoute><QuoteForm /></ProtectedRoute>} />
          <Route path="/preview/:quoteId" element={<ProtectedRoute><QuotePreview /></ProtectedRoute>} />
          <Route path="/history" element={<ProtectedRoute><QuoteHistory /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        </Routes>
      </main>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AdminProvider>
            <AppRouter />
          </AdminProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
